import { combineEpics, ofType } from "redux-observable"
import { delay, filter, flatMap, map, startWith } from "rxjs/operators"
import { EMPTY, of } from "rxjs"
import { Epic } from "../../epics"

import * as actions from "./actions"
import { doesStringContainHTML } from "../../util/Strings"
import { GlobalState } from "../../store"
import { guid } from "../../util/Guid"
import { Labels } from "../../util/Translations"
import { INotificationMessage } from "./index"

export const addMessageTriggerEpic: Epic = (action$) =>
    action$.pipe(
        ofType(actions.ADD_MESSAGE_TRIGGER),
        flatMap((action: actions.NotificationMessageTrigger) => {
            if (action.autoRemove !== undefined) {
                return of(actions.removeMessage(action.message)).pipe(
                    delay(action.autoRemove),
                    startWith(actions.addMessage(action.message)),
                )
            } else {
                return of(actions.addMessage(action.message))
            }
        }),
    )

export const appendPreviousMessageEpic: Epic = (action$) =>
    action$.pipe(
        ofType(actions.APPEND_PREVIOUS_MESSAGE),
        flatMap(() => of(actions.flash(true))),
    )

export const appendPreviousMessageTriggerEpic: Epic = (action$) =>
    action$.pipe(
        ofType(actions.APPEND_PREVIOUS_MESSAGE_TRIGGER),
        flatMap((action: actions.NotificationMessageTrigger) => {
            if (action.autoRemove !== undefined) {
                return of(actions.removeMessage(action.message)).pipe(
                    delay(action.autoRemove),
                    startWith(actions.appendPreviousMessage(action.message)),
                )
            } else {
                return of(actions.appendPreviousMessage(action.message))
            }
        }),
    )

export const addErrorNotificationEpic: Epic = (
    action$,
    _state$,
    _dependencies$,
) =>
    action$.pipe(
        ofType(actions.ADD_ERROR_NOTIFICATION),
        flatMap((action: actions.NewNotificationMessage) => {
            const { message, attrs, overwriteExisting, autoRemove } = action
            return of(
                actions.appendOrUpdatePreviousMessage(
                    message,
                    "error",
                    overwriteExisting,
                    autoRemove,
                    attrs,
                ),
            )
        }),
    )

export const addInformationNotificationEpic: Epic = (
    action$,
    _state$,
    _dependencies$,
) =>
    action$.pipe(
        ofType(actions.ADD_INFORMATION_NOTIFICATION),
        flatMap((action: actions.NewNotificationMessage) => {
            const { message, attrs, overwriteExisting, autoRemove } = action
            return of(
                actions.appendOrUpdatePreviousMessage(
                    message,
                    "information",
                    overwriteExisting,
                    autoRemove,
                    attrs,
                ),
            )
        }),
    )

export const addWarningNotificationEpic: Epic = (
    action$,
    _state$,
    _dependencies$,
) =>
    action$.pipe(
        ofType(actions.ADD_WARNING_NOTIFICATION),
        flatMap((action: actions.NewNotificationMessage) => {
            const { message, attrs, overwriteExisting, autoRemove } = action
            return of(
                actions.appendOrUpdatePreviousMessage(
                    message,
                    "warning",
                    overwriteExisting,
                    autoRemove,
                    attrs,
                ),
            )
        }),
    )

const doesMostRecentMessageHasType = (
    messageType: string,
    state: GlobalState,
): boolean => {
    const previousNotification = state.notifications.messages[0]
    return previousNotification && previousNotification.type === messageType
}

function decodeHtml(html: string): string {
    const txt = document.createElement("textarea")
    txt.innerHTML = html
    return txt.value
}

export const appendOrUpdatePreviousMessageEpic: Epic = (action$, state$) =>
    action$.pipe(
        ofType(actions.APPEND_OR_UPDATE_PREVIOUS_MESSAGE),
        flatMap((action: actions.NewTypedNotificationMessage) => {
            let { message } = action
            const { messageType, attrs, overwriteExisting, autoRemove } = action

            // we create a div and fill it with the content of the message.
            const container = document.createElement("div")
            container.innerHTML = message

            // extract possible script(s) from the container
            const scriptTags = Array.from(
                container.getElementsByTagName("script"),
            )

            // store the script(s)
            const javascripts = scriptTags.map((script) => script.innerHTML)

            // remove content again from script tags
            scriptTags.forEach((script) => script.remove())

            // what's left is now the message (decoded to properly show html characters < > and &)
            message = decodeHtml(container.innerHTML)

            // remove linebreaks from the container to check if it only contained linebreaks
            Array.from(container.getElementsByTagName("br")).forEach((child) =>
                child.remove(),
            )

            // execute each script separately
            try {
                javascripts.forEach((script) => {
                    new Function(script)()
                })
            } catch (error) {
                console.error(error)
                const { InvalidScriptExecutionFailure } = Labels
                return of(
                    actions.addErrorNotification(InvalidScriptExecutionFailure),
                )
            }

            // if the message was only code to be executed, we can already return here
            if (message.length === 0 || container.innerHTML.length === 0) {
                return EMPTY
            }

            const messageObject: INotificationMessage = {
                uuid: guid(),
                content: message,
                type: messageType,
                isContentHtml: doesStringContainHTML(message),
                attrs: attrs,
            }

            const mostRecentMessageHadSameType = doesMostRecentMessageHasType(
                messageType,
                state$.value,
            )

            if (!mostRecentMessageHadSameType) {
                return of(actions.addMessageTrigger(messageObject, autoRemove))
            } else if (overwriteExisting) {
                return of(
                    actions.overwritePreviousMessageTrigger(
                        messageObject,
                        autoRemove,
                    ),
                )
            }

            return of(
                actions.appendPreviousMessageTrigger(messageObject, autoRemove),
            )
        }),
    )

export const overwritePreviousMessageEpic: Epic = (action$) =>
    action$.pipe(
        ofType(actions.OVERWRITE_PREVIOUS_MESSAGE),
        flatMap(() => of(actions.flash(true))),
    )

export const overwritePreviousMessageTriggerEpic: Epic = (action$) =>
    action$.pipe(
        ofType(actions.OVERWRITE_PREVIOUS_MESSAGE_TRIGGER),
        flatMap((action: actions.NotificationMessageTrigger) => {
            if (action.autoRemove !== undefined) {
                return of(actions.removeMessage(action.message)).pipe(
                    delay(action.autoRemove),
                    startWith(actions.overwritePreviousMessage(action.message)),
                )
            } else {
                return of(actions.overwritePreviousMessage(action.message))
            }
        }),
    )

export const notificationFlashingEpic: Epic = (
    action$,
    _state$,
    { notifications: { flashingDelay } },
) =>
    action$.pipe(
        ofType(actions.FLASH),
        filter(
            ({ isFlashing }: actions.NotificationMessageFlash) => isFlashing,
        ),
        delay(flashingDelay),
        map((action: actions.NotificationMessageFlash) => ({
            ...action,
            isFlashing: false,
        })),
    )

export default combineEpics(
    addMessageTriggerEpic,
    appendPreviousMessageEpic,
    appendPreviousMessageTriggerEpic,
    notificationFlashingEpic,
    overwritePreviousMessageEpic,
    overwritePreviousMessageTriggerEpic,
    appendOrUpdatePreviousMessageEpic,
    addErrorNotificationEpic,
    addInformationNotificationEpic,
    addWarningNotificationEpic,
)
